import { Trans, useTranslation } from "react-i18next";

import { ProfileCard } from "@/components/Layout/BaseLayouts/ProfileCard/ProfileCard";
import {
  DeductionChip,
  PriceRow,
} from "@/components/Portal/DeviceManagement/SubscriptionDetails/SubscriptionDisplay/SubscriptionPrice/SubscriptionPrice";
import { availableCurrencies, getCurrency } from "@/utils/translationHelpers";

export const SubscriptionDueNow = ({
  icon,
  totalOnceAmount,
  discountOnceAmount,
  discountName,
  discountEndDate,
  credit,
  creditTimeRemaining,
  currency,
  dataRateName,
  dataRatePrice,
  voiceOptionPrice,
  billingPeriod,
  watchSimName,
  watchSimPrice,
  recurringAmount,
  recurringDate,
  recurringBillingPeriod,
  recurringCaption,
}: {
  icon?: JSX.Element;
  totalOnceAmount: number;
  discountOnceAmount?: number;
  discountName?: string;
  discountEndDate?: Date;
  credit?: number;
  creditTimeRemaining?: number;
  currency: availableCurrencies;
  dataRateName?: string;
  dataRatePrice?: number;
  voiceOptionPrice?: number;
  billingPeriod: string;
  watchSimName?: string;
  watchSimPrice?: number;
  recurringAmount?: number;
  recurringDate?: string;
  recurringBillingPeriod?: string;
  recurringCaption?: string;
}) => {
  const { t } = useTranslation();
  const { t: tCheckout } = useTranslation("portal", {
    keyPrefix: "cockpit.managementTile.watchSim.checkout",
  });

  const showVoiceOption =
    !dataRateName?.startsWith("Flat Mobile") &&
    voiceOptionPrice !== undefined &&
    voiceOptionPrice >= 0;

  const resolvedDiscountName =
    discountName ?? t("Onboarding.sections.checkout.summary.discount");

  return (
    <ProfileCard
      variant="white"
      className="self-start"
      title={tCheckout("nowToPay")}
      actions={icon ? <div id="device-icon">{icon}</div> : undefined}
    >
      <div className="flex w-full flex-col">
        {!!dataRateName &&
          dataRatePrice !== undefined &&
          dataRatePrice >= 0 && (
            <PriceRow
              withBackdrop
              variant="strong"
              label={dataRateName}
              value={getCurrency(dataRatePrice, currency)}
            />
          )}

        {showVoiceOption && (
          <PriceRow
            label={
              <>
                {t("common.device.attributes.voiceOption")}
                <br />
                <span className="text-body-xs font-regular leading-md">
                  {billingPeriod}
                </span>
              </>
            }
            value={getCurrency(voiceOptionPrice, currency)}
          />
        )}

        {!!watchSimName &&
          watchSimPrice !== undefined &&
          watchSimPrice >= 0 && (
            <PriceRow
              label={
                <>
                  {watchSimName}
                  <br />
                  <span className="text-body-xs font-regular leading-md">
                    {billingPeriod}
                  </span>
                </>
              }
              value={getCurrency(watchSimPrice, currency)}
            />
          )}

        {!!discountOnceAmount && discountOnceAmount > 0 && (
          <PriceRow
            label={
              <>
                {resolvedDiscountName}
                {discountEndDate && (
                  <Trans
                    t={t}
                    i18nKey="Onboarding.sections.checkout.summary.until_date"
                    values={{
                      date: discountEndDate.toLocaleDateString("de-CH"),
                    }}
                    components={[
                      <span
                        key="until_date"
                        className="text-foreground-error"
                      />,
                    ]}
                  />
                )}
              </>
            }
            value={
              <span data-testid="subscription-checkout-discount">
                <DeductionChip amount={discountOnceAmount} />
              </span>
            }
          />
        )}

        {!!creditTimeRemaining && creditTimeRemaining > 0 && (
          <PriceRow
            withDivider
            label={tCheckout("creditRemainingTime")}
            value={
              <span data-testid="subscription-checkout-credit-time-remaining">
                <DeductionChip amount={creditTimeRemaining} />
              </span>
            }
          />
        )}

        {!!credit && credit > 0 && (
          <PriceRow
            withDivider
            label={tCheckout("creditOther")}
            value={
              <span data-testid="subscription-checkout-credit">
                <DeductionChip amount={credit} />
              </span>
            }
          />
        )}

        <PriceRow
          withTopDivider
          variant="strong"
          label={
            <>
              <span>{tCheckout("sum")}</span>
              <br />
              <span className="text-body-xs font-regular leading-md">
                {t("Onboarding.sections.checkout.summary.taxIncluded")}
              </span>
            </>
          }
          value={
            <>
              <span data-testid="subscription-checkout-total">
                {getCurrency(Math.max(totalOnceAmount, 0), currency)}
              </span>
              {recurringCaption && (
                <span className="text-body-xs font-regular leading-md">
                  {recurringCaption}
                </span>
              )}
            </>
          }
        />

        {recurringAmount !== undefined &&
          recurringAmount !== Math.max(totalOnceAmount, 0) && (
            <PriceRow
              withBackdrop
              variant="strong"
              label={
                <>
                  <span>{tCheckout("thereafter")}</span>
                  {recurringDate && (
                    <>
                      <br />
                      <span className="text-body-xs font-regular leading-md">
                        {recurringDate}
                      </span>
                    </>
                  )}
                </>
              }
              value={
                <>
                  <span data-testid="subscription-checkout-recurring">
                    {getCurrency(recurringAmount, currency)}
                  </span>
                  {recurringBillingPeriod && (
                    <span className="text-body-xs font-regular leading-md">
                      {recurringBillingPeriod}
                    </span>
                  )}
                </>
              }
            />
          )}
      </div>
    </ProfileCard>
  );
};
